/**
 * "Later" — Slack's saved items and reminders (contracts later.ts): the three tabs (In progress / Archived /
 * Completed), reminder presets ("In 20 minutes" … "Next week"), due / overdue state, optimistic list edits, and the
 * `/remind me <what> <when>` parser. Times are computed in the person's local time via `offsetMinutes` (minutes to add
 * to UTC for local time, e.g. -180 for São Paulo), so the same inputs give the same answers on every platform.
 */
import type { LaterItem, LaterState, ReminderRepeat } from "@bundle/contracts";

export const LATER_TABS: readonly LaterState[] = ["in_progress", "archived", "completed"];

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** Slack's reminder presets, in menu order ("Custom…" is the UI's own date/time picker). */
export const REMIND_PRESETS = ["in20m", "in1h", "in3h", "tomorrow", "nextWeek"] as const;
export type RemindPreset = (typeof REMIND_PRESETS)[number];

/** Local hour reminders for "Tomorrow" / "Next week" land on (Slack: 9:00 AM). */
export const MORNING_HOUR = 9;

/** Start of the local day containing `at`, as a UTC timestamp. */
function localDayStart(at: number, offsetMinutes: number): number {
  const local = at + offsetMinutes * MIN;
  return local - (((local % DAY) + DAY) % DAY) - offsetMinutes * MIN;
}

/** Local weekday of `at` (0 = Sunday). */
function localWeekday(at: number, offsetMinutes: number): number {
  return new Date(at + offsetMinutes * MIN).getUTCDay();
}

/** When a preset reminds: +20 min, +1 h, +3 h, tomorrow 9:00, next Monday 9:00 (local). */
export function presetTime(preset: RemindPreset, now: number, offsetMinutes: number): number {
  switch (preset) {
    case "in20m":
      return now + 20 * MIN;
    case "in1h":
      return now + HOUR;
    case "in3h":
      return now + 3 * HOUR;
    case "tomorrow":
      return localDayStart(now, offsetMinutes) + DAY + MORNING_HOUR * HOUR;
    case "nextWeek": {
      const wd = localWeekday(now, offsetMinutes);
      const days = ((8 - wd) % 7) || 7;
      return localDayStart(now, offsetMinutes) + days * DAY + MORNING_HOUR * HOUR;
    }
  }
}

/** Whether the item's reminder is due (it stays In progress, shown as overdue, until handled). */
export function isOverdue(item: LaterItem, now: number): boolean {
  return item.state === "in_progress" && item.remindAt !== undefined && item.remindAt <= now;
}

/** One tab's items: In progress and Archived by save time, Completed by completion time (newest first; ties by id). */
export function laterTab(items: readonly LaterItem[], tab: LaterState): LaterItem[] {
  const key = (i: LaterItem) => (tab === "completed" ? (i.completedAt ?? i.updatedAt) : i.savedAt);
  return items.filter((i) => i.state === tab).sort((a, b) => key(b) - key(a) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

/** Items per tab (the tab labels' counts). */
export function tabCounts(items: readonly LaterItem[]): Record<LaterState, number> {
  const out: Record<LaterState, number> = { in_progress: 0, archived: 0, completed: 0 };
  for (const i of items) out[i.state] += 1;
  return out;
}

/** In progress items whose reminder is due (the sidebar badge). */
export function overdueCount(items: readonly LaterItem[], now: number): number {
  return items.filter((i) => isOverdue(i, now)).length;
}

/** The item saving this message, if any (the row's "Saved for later" line; Save ↔ Remove). */
export function itemForMessage(items: readonly LaterItem[], messageId: string): LaterItem | undefined {
  return items.find((i) => i.messageId === messageId);
}

/** Adds or replaces (by id, else by message) an item; newest first by save time. */
export function upsertItem(items: readonly LaterItem[], item: LaterItem): LaterItem[] {
  const rest = items.filter((i) => i.id !== item.id && (item.messageId === undefined || i.messageId !== item.messageId));
  return [item, ...rest].sort((a, b) => b.savedAt - a.savedAt);
}

export function removeItem(items: readonly LaterItem[], id: string): LaterItem[] {
  return items.filter((i) => i.id !== id);
}

/**
 * The item after a state / reminder change, as the server will store it: completing stamps `completedAt` (leaving
 * Completed clears it); a new or cleared reminder clears `remindedAt`.
 */
export function patchItem(item: LaterItem, patch: { state?: LaterState; remindAt?: number | null; stopRepeat?: boolean }, now: number): LaterItem {
  const next: LaterItem = { ...item, updatedAt: now };
  // Clearing the reminder (or asking to) stops a recurring one.
  if (patch.stopRepeat === true || patch.remindAt === null) delete next.repeat;
  if (patch.state !== undefined && patch.state !== item.state) {
    next.state = patch.state;
    if (patch.state === "completed") next.completedAt = now;
    else delete next.completedAt;
  }
  if (patch.remindAt !== undefined) {
    delete next.remindedAt;
    if (patch.remindAt === null) delete next.remindAt;
    else next.remindAt = patch.remindAt;
  }
  return next;
}

// ---------- /remind ----------

/** Who a `/remind` is for: me (absent), a person (`<@id>` chip) or a conversation (`<#id>` chip). */
export type RemindTarget = { kind: "user"; userId: string } | { kind: "channel"; channelId: string };

export type RemindParse =
  /** `target` absent = me; `repeat` = a recurring reminder whose first time is `at`. */
  | { ok: true; text: string; at: number; target?: RemindTarget; repeat?: ReminderRepeat }
  /** usage: no "what" or no "when"; target: "@ana" / "#general" typed as text, not picked; past: the time has passed. */
  | { ok: false; error: "usage" | "target" | "past" };

/** Whether a draft is a `/remind` command (the composer runs it instead of posting it). */
export function isRemindCommand(draft: string): boolean {
  return /^\/remind(\s|$)/i.test(draft.trim());
}

const UNIT_MS: Record<string, number> = {
  m: MIN, min: MIN, mins: MIN, minute: MIN, minutes: MIN, minuto: MIN, minutos: MIN,
  h: HOUR, hr: HOUR, hrs: HOUR, hour: HOUR, hours: HOUR, hora: HOUR, horas: HOUR,
  d: DAY, day: DAY, days: DAY, dia: DAY, dias: DAY,
  w: 7 * DAY, week: 7 * DAY, weeks: 7 * DAY, semana: 7 * DAY, semanas: 7 * DAY,
};

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/** "3pm", "3:30 pm", "15:00", "15h" → minutes after local midnight. */
function clockMinutes(s: string): number | undefined {
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm|h)?$/i.exec(s.trim());
  if (m === null) return undefined;
  let h = Number(m[1]);
  const min = m[2] === undefined ? 0 : Number(m[2]);
  const ap = m[3]?.toLowerCase();
  if (min > 59) return undefined;
  if (ap === "am" || ap === "pm") {
    if (h < 1 || h > 12) return undefined;
    h = (h % 12) + (ap === "pm" ? 12 : 0);
  } else if (h > 23) return undefined;
  if (ap === undefined && m[2] === undefined) return undefined; // a bare number isn't a time
  return h * 60 + min;
}

/** The next local occurrence of a clock time (today if still ahead, else tomorrow). */
function nextAt(minutes: number, now: number, offsetMinutes: number, dayOffset?: number): number {
  const base = localDayStart(now, offsetMinutes) + minutes * MIN;
  if (dayOffset !== undefined) return base + dayOffset * DAY;
  return base > now ? base : base + DAY;
}

/** Finds a "when" phrase at the end (or start) of `rest`; → the time and the text without it. */
function extractWhen(rest: string, now: number, offsetMinutes: number): { at: number; text: string } | undefined {
  const patterns: { re: RegExp; at: (m: RegExpExecArray) => number | undefined }[] = [
    { re: /(?:^|\s)(?:in|em|daqui a)\s+(\d+|an?|one|um|uma)\s*([a-z]+)$/i, at: (m) => {
      const n = /^\d+$/.test(m[1]!) ? Number(m[1]) : 1;
      const unit = UNIT_MS[m[2]!.toLowerCase()];
      return unit === undefined || n <= 0 ? undefined : now + n * unit;
    } },
    { re: /(?:^|\s)(tomorrow|amanhã|amanha)(?:\s+(?:at|às|as)\s+([\d:apmh ]+))?$/i, at: (m) => {
      const t = m[2] === undefined ? MORNING_HOUR * 60 : clockMinutes(m[2]);
      return t === undefined ? undefined : nextAt(t, now, offsetMinutes, 1);
    } },
    { re: /(?:^|\s)(?:today|hoje)\s+(?:at|às|as)\s+([\d:apmh ]+)$/i, at: (m) => {
      const t = clockMinutes(m[1]!);
      return t === undefined ? undefined : nextAt(t, now, offsetMinutes, 0);
    } },
    { re: /(?:^|\s)(?:next week|semana que vem|próxima semana|proxima semana)$/i, at: () => presetTime("nextWeek", now, offsetMinutes) },
    { re: /\bon\s+(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+at\s+([\d:apmh ]+))?$/i, at: (m) => {
      const t = m[4] === undefined ? MORNING_HOUR * 60 : clockMinutes(m[4]);
      const day = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      if (t === undefined || Number.isNaN(day) || new Date(day).getUTCDate() !== Number(m[3])) return undefined;
      return day + t * MIN - offsetMinutes * MIN;
    } },
    { re: /(?:^|\s)(?:on\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)(?:\s+at\s+([\d:apmh ]+))?$/i, at: (m) => {
      const target = WEEKDAYS.indexOf(m[1]!.toLowerCase());
      const t = m[2] === undefined ? MORNING_HOUR * 60 : clockMinutes(m[2]);
      if (t === undefined) return undefined;
      let days = (target - localWeekday(now, offsetMinutes) + 7) % 7;
      if (days === 0 && nextAt(t, now, offsetMinutes, 0) <= now) days = 7;
      return nextAt(t, now, offsetMinutes, days);
    } },
    { re: /(?:^|\s)(?:at|às|as)\s+([\d:apmh ]+)$/i, at: (m) => {
      const t = clockMinutes(m[1]!);
      return t === undefined ? undefined : nextAt(t, now, offsetMinutes);
    } },
  ];
  for (const p of patterns) {
    const m = p.re.exec(rest);
    if (m === null) continue;
    const at = p.at(m);
    if (at === undefined) continue;
    return { at, text: rest.slice(0, m.index).trim() };
  }
  // "/remind me in 20 minutes to stretch": the phrase first.
  const lead = /^(?:in|em)\s+(\d+)\s*([a-z]+)\s+(.+)$/i.exec(rest);
  if (lead !== null) {
    const unit = UNIT_MS[lead[2]!.toLowerCase()];
    if (unit !== undefined) return { at: now + Number(lead[1]) * unit, text: lead[3]!.trim() };
  }
  return undefined;
}

// ---------- Recurring reminders ----------

const PT_WEEKDAYS: Record<string, number> = { domingo: 0, segunda: 1, "terça": 2, terca: 2, quarta: 3, quinta: 4, sexta: 5, "sábado": 6, sabado: 6 };

function weekdayIndex(word: string): number | undefined {
  // "mondays", "segunda-feira", "terças": no weekday name ends in "s" in either language.
  const w = word.toLowerCase().replace(/-feiras?$/, "").replace(/s$/, "");
  const en = WEEKDAYS.indexOf(w);
  if (en >= 0) return en;
  return PT_WEEKDAYS[w];
}

/** Days in the month of a UTC date (year, month 0-based). */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** Whether a local day (as a UTC-midnight date) is one the rule fires on. */
function repeatsOn(r: ReminderRepeat, day: Date): boolean {
  const wd = day.getUTCDay();
  switch (r.every) {
    case "day":
      return true;
    case "weekday":
      return wd >= 1 && wd <= 5;
    case "week":
      return wd === (r.weekday ?? 1);
    case "month":
      return day.getUTCDate() === Math.min(r.monthDay ?? 1, daysInMonth(day.getUTCFullYear(), day.getUTCMonth()));
  }
}

/** The first time the recurring reminder fires strictly after `after` (its local time on a matching local day). */
export function nextOccurrence(r: ReminderRepeat, after: number): number {
  const off = r.offsetMinutes * MIN;
  const local = after + off;
  const day0 = local - (((local % DAY) + DAY) % DAY);
  for (let i = 0; i <= 62; i += 1) {
    const day = day0 + i * DAY;
    if (!repeatsOn(r, new Date(day))) continue;
    const at = day + r.minutes * MIN - off;
    if (at > after) return at;
  }
  return after + DAY; // unreachable: every rule matches within two months
}

/**
 * Finds "every … [at T]" / "at T every …" at the end of `rest` (plus pt-BR "todo dia", "todos os dias úteis", "toda
 * segunda"): → the rule (default 9:00) and the text before it.
 */
function extractRepeat(rest: string, offsetMinutes: number): { repeat: ReminderRepeat; text: string } | undefined {
  const TIME = "([\\d:apmh ]+?)";
  const RULE = "(every|todo|toda|todos os|todas as)\\s+(day|weekday|week|month|dia útil|dia util|dias úteis|dias uteis|dia|dias|[a-zçáé]+(?:-feira)?s?)(?:\\s+on\\s+the\\s+(\\d{1,2})(?:st|nd|rd|th)?)?";
  const AT = "(?:at|às|as)";
  const forms: { re: RegExp; rule: number; time: number }[] = [
    { re: new RegExp(`(?:^|\\s)${AT}\\s+${TIME}\\s+${RULE}$`, "i"), rule: 2, time: 1 },
    { re: new RegExp(`(?:^|\\s)${RULE}(?:\\s+${AT}\\s+${TIME})?$`, "i"), rule: 1, time: 4 },
  ];
  for (const f of forms) {
    const m = f.re.exec(rest);
    if (m === null) continue;
    const unit = m[f.rule + 1]!.toLowerCase();
    const monthDay = m[f.rule + 2] === undefined ? undefined : Number(m[f.rule + 2]);
    const minutes = m[f.time] === undefined ? MORNING_HOUR * 60 : clockMinutes(m[f.time]!);
    if (minutes === undefined) continue;
    let repeat: ReminderRepeat;
    if (unit === "day" || unit === "dia" || unit === "dias") repeat = { every: "day", minutes, offsetMinutes };
    else if (unit === "weekday" || /^dias? [úu]te(is|l)$/.test(unit)) repeat = { every: "weekday", minutes, offsetMinutes };
    else if (unit === "month") {
      if (monthDay !== undefined && (monthDay < 1 || monthDay > 31)) continue;
      repeat = { every: "month", monthDay: monthDay ?? 1, minutes, offsetMinutes };
    } else {
      const weekday = unit === "week" ? undefined : weekdayIndex(unit);
      if (unit !== "week" && weekday === undefined) continue;
      repeat = { every: "week", weekday: weekday ?? -1, minutes, offsetMinutes };
    }
    return { repeat, text: rest.slice(0, m.index).trim() };
  }
  return undefined;
}

/** A target chip at the start of the command: `<@id>` / `<@id|name>` → a person, `<#id>` → a conversation. */
function parseTarget(word: string): RemindTarget | "me" | "text" | undefined {
  const lower = word.toLowerCase();
  if (lower === "me" || lower === "eu") return "me";
  const user = /^<@([^>|]+)(?:\|[^>]*)?>$/.exec(word);
  if (user !== null) return { kind: "user", userId: user[1]! };
  const ch = /^<#([^>|]+)(?:\|[^>]*)?>$/.exec(word);
  if (ch !== null) return { kind: "channel", channelId: ch[1]! };
  if (word.startsWith("@") || word.startsWith("#") || word.startsWith("<")) return "text";
  return undefined;
}

/**
 * `/remind me to stretch in 20 minutes`, `/remind me call Ana tomorrow at 3pm`, `/remind me "ship it" at 17:00`,
 * `/remind me review on friday`, `/remind me in 2 hours to check the deploy` (plus a few pt-BR words: amanhã, em 20
 * minutos, às 15h). Only `me` is supported as the target (Slack's @someone / #channel reminders are not built).
 */
export function parseRemind(draft: string, now: number, offsetMinutes: number): RemindParse {
  const body = draft.trim().replace(/^\/remind\b/i, "").trim();
  const head = /^(<[@#][^>]*>|\S+)\s*(.*)$/s.exec(body);
  if (head === null) return { ok: false, error: "usage" };
  const who = parseTarget(head[1]!);
  if (who === "text") return { ok: false, error: "target" };
  if (who === undefined) return { ok: false, error: "usage" };
  const rest = head[2]!.replace(/\s+/g, " ").trim();
  const rep = extractRepeat(rest, offsetMinutes);
  if (rep !== undefined && rep.repeat.every === "week" && rep.repeat.weekday === -1) rep.repeat.weekday = localWeekday(now, offsetMinutes);
  const when = rep === undefined ? extractWhen(rest, now, offsetMinutes) : { at: nextOccurrence(rep.repeat, now), text: rep.text };
  if (when === undefined) return { ok: false, error: "usage" };
  let text = when.text.replace(/^(?:to|de|para)\s+/i, "").trim();
  const quoted = /^"(.+)"$/.exec(text) ?? /^“(.+)”$/.exec(text);
  if (quoted !== null) text = quoted[1]!.trim();
  if (text === "") return { ok: false, error: "usage" };
  if (when.at <= now) return { ok: false, error: "past" };
  const out: RemindParse = { ok: true, text, at: when.at };
  if (who !== "me") out.target = who;
  if (rep !== undefined) out.repeat = rep.repeat;
  return out;
}
